/* mb-cine-scrubber.js — WebCodecs frame scrubber for the cinematic hero
   ---------------------------------------------------------------------
   DESKTOP ONLY. Phones run the four portrait beats through drawStills instead.

   Why this exists. The hero used to scrub with `video.currentTime`. That is
   asynchronous, snaps to keyframes, decodes badly in reverse, and is the reason
   the masters had to be encoded all-intra (240 keyframes / 240 frames) just to
   make seeks land — which is where 16.6MB for 16 seconds comes from.

   It also cannot be tested locally at all: `shopify theme dev` answers a Range
   request with HTTP 200 and the whole file and sends no Accept-Ranges, so
   `video.seekable` is [0,0] and the scrub is frozen. Measured 2026-09-04.

   This decodes the exact frame instead. The file is fetched once as an
   ArrayBuffer, demuxed with MP4Box, and each target frame is handed to a
   VideoDecoder on demand. No Range requests, so it works on the dev server AND
   on the CDN. All-intra means every sample is a keyframe, so a seek is one
   chunk with no dependency chain to rebuild — the current encode is already
   ideal for this, and re-encoding to a normal GOP is a later size win.

   Technique adapted from github.com/yojahny55/cinematic-scroll-kit (MIT),
   `templates/cinematic-scrubber.js`. Rewritten here for two clips composited on
   one canvas, and for lazy MP4Box loading from theme assets rather than a CDN.

   Public API:
     const s = new MbCineScrubber(url, { mp4boxUrl });
     await s.init();          // fetch + demux + configure
     s.seek(0.42);            // request the frame at 42% of this clip
     s.paintTo(ctx, w, h, alpha);
     s.destroy();
*/
(function (w) {
  'use strict';

  var _mp4box = null;

  function loadMp4Box(url) {
    if (w.MP4Box) return Promise.resolve();
    if (_mp4box) return _mp4box;
    _mp4box = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = url; s.async = true;
      s.onload = res;
      s.onerror = function () { rej(new Error('mp4box failed to load')); };
      document.head.appendChild(s);
    });
    return _mp4box;
  }

  function supported() {
    return typeof w.VideoDecoder === 'function'
        && typeof w.EncodedVideoChunk === 'function'
        && typeof w.createImageBitmap === 'function';
  }

  /* MP4Box hands the codec description back in different shapes depending on the
     box layout; walk the sample entry for avcC/hvcC rather than guessing. */
  function description(file, track) {
    var trak = file.getTrackById(track.id);
    var entries = trak.mdia.minf.stbl.stsd.entries;
    for (var i = 0; i < entries.length; i++) {
      var e = entries[i];
      var box = e.avcC || e.hvcC || e.vpcC || e.av1C;
      if (box) {
        /* mp4box.all.min.js exposes DataStream as its own global, not as a
           property of MP4Box. Check both so either build works. */
        var Stream = w.DataStream || (w.MP4Box && w.MP4Box.DataStream);
        if (!Stream) return undefined;
        var ds = new Stream(undefined, 0, Stream.BIG_ENDIAN);
        box.write(ds);
        return new Uint8Array(ds.buffer, 8); // strip the box header
      }
    }
    return undefined;
  }

  function MbCineScrubber(url, opts) {
    opts = opts || {};
    this.url = url;
    this.mp4boxUrl = opts.mp4boxUrl;
    this.onReady = opts.onReady || function () {};
    this.onFrame = opts.onFrame || function () {};
    this.onError = opts.onError || function () {};
    this.totalSamples = 0;
    this.abort = new AbortController();
    this.samples = [];
    this.decoder = null;
    this.ready = false;
    this.dead = false;
    this.width = 0;
    this.height = 0;
    this.want = -1;        // frame index most recently requested
    this.have = -1;        // frame index currently on the buffer
    this.inflight = false;
    this.buf = null;       // OffscreenCanvas holding the last decoded frame
    this.bctx = null;
  }

  /* Feed fast-start MP4 data to the demuxer as it arrives. Frame indices use
     the track's full sample count, never the growing downloaded sample list. */
  MbCineScrubber.prototype.init = async function () {
    var self = this;
    await loadMp4Box(this.mp4boxUrl);
    if (this.dead) return;
    var file = w.MP4Box.createFile();
    var problem = null;
    var offset = 0;
    file.onError = function (e) { problem = new Error('demux: ' + e); };
    file.onReady = function (info) {
      try {
        var track = info.videoTracks[0];
        if (!track) throw new Error('no video track');
        self.width = track.video.width;
        self.height = track.video.height;
        self.totalSamples = track.nb_samples;
        self.decoder = new w.VideoDecoder({
          output: function (f) { self._onFrame(f); },
          error: function (e) { self.onError(e); }
        });
        self.decoder.configure({
          codec: track.codec,
          codedWidth: self.width,
          codedHeight: self.height,
          description: description(file, track),
          optimizeForLatency: true
        });
        self.buf = (typeof OffscreenCanvas === 'function')
          ? new OffscreenCanvas(self.width, self.height)
          : Object.assign(document.createElement('canvas'), { width: self.width, height: self.height });
        self.bctx = self.buf.getContext('2d', { alpha: false });
        if (!self.bctx) throw new Error('no canvas context');
        self.ready = true;
        file.setExtractionOptions(track.id, null, { nbSamples: 1 });
        self.onReady(self);
        file.start();
      } catch (e) { problem = e; }
    };
    file.onSamples = function (id, user, arr) {
      for (var i = 0; i < arr.length; i++) {
        var sample = arr[i];
        self.samples.push({
          type: sample.is_sync ? 'key' : 'delta',
          timestamp: (sample.cts * 1e6) / sample.timescale,
          duration: (sample.duration * 1e6) / sample.timescale,
          data: sample.data
        });
      }
      self._pump(); // A requested frame may have just arrived after scrolling stopped.
    };
    var response = await fetch(this.url, { signal: this.abort.signal });
    if (!response.ok) throw new Error('scrubber: HTTP ' + response.status);
    var append = function (buffer) {
      if (self.dead) return;
      buffer.fileStart = offset;
      offset += buffer.byteLength;
      file.appendBuffer(buffer);
      if (problem) throw problem;
    };
    if (response.body && response.body.getReader) {
      var reader = response.body.getReader();
      try {
        while (!this.dead) {
          var chunk = await reader.read();
          if (chunk.done) break;
          append(chunk.value.buffer.slice(chunk.value.byteOffset,
            chunk.value.byteOffset + chunk.value.byteLength));
        }
      } finally { reader.releaseLock(); }
    } else {
      append(await response.arrayBuffer());
    }
    if (this.dead) return;
    file.flush();
    if (problem) throw problem;
    if (!this.ready || this.samples.length !== this.totalSamples) {
      throw new Error('incomplete video samples');
    }
    return this;
  };

  MbCineScrubber.prototype._onFrame = function (frame) {
    if (this.dead) { frame.close(); return; }
    this.bctx.drawImage(frame, 0, 0, this.width, this.height);
    this.have = this.pending;
    frame.close();
    this.inflight = false;
    this.onFrame();
    /* a newer request landed while this one was decoding: chase it */
    if (this.want !== this.have) this._pump();
  };

  MbCineScrubber.prototype._pump = function () {
    if (!this.ready || this.inflight || this.dead) return;
    if (this.want === this.have || this.want < 0) return;
    var s = this.samples[this.want];
    if (!s) return;
    this.pending = this.want;
    this.inflight = true;
    try {
      this.decoder.decode(new w.EncodedVideoChunk({
        type: s.type, timestamp: s.timestamp, duration: s.duration, data: s.data
      }));
    } catch (e) {
      this.inflight = false;
      this.onError(e);
    }
  };

  /* t is 0..1 through THIS clip */
  MbCineScrubber.prototype.seek = function (t) {
    if (!this.ready) return;
    var n = this.totalSamples;
    var i = Math.max(0, Math.min(n - 1, Math.round(t * (n - 1))));
    if (i === this.want) return;
    this.want = i;
    this._pump();
  };

  MbCineScrubber.prototype.paintTo = function (ctx, cw, ch, alpha) {
    if (!this.ready || this.have < 0 || alpha <= 0.001) return false;
    /* cover-fit, same as object-fit: cover on the old <video> */
    var sc = Math.max(cw / this.width, ch / this.height);
    var dw = this.width * sc, dh = this.height * sc;
    ctx.globalAlpha = alpha;
    ctx.drawImage(this.buf, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    ctx.globalAlpha = 1;
    return true;
  };

  MbCineScrubber.prototype.destroy = function () {
    this.dead = true;
    this.abort.abort();
    try { if (this.decoder && this.decoder.state !== 'closed') this.decoder.close(); } catch (e) {}
    this.samples = [];
    this.buf = null;
  };

  w.MbCineScrubber = MbCineScrubber;
  w.MbCineScrubber.supported = supported;
}(window));
